import { FormControlLabel, makeStyles, Snackbar, styled, Switch, withStyles } from "@material-ui/core";
import CheckIcon from '@material-ui/icons/Check';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { Alert, AlertTitle } from "@material-ui/lab";
import { Linked } from "@type-r/models";
import { useIO, useLinked, useModel } from "@type-r/react";
import {
    Column,
    div, Flex,
    FormLabel,
    FormSelect,
    Input,
    Loading,
    Option,
    Page,
    PopupIODialog,
    PrimaryButton, SelectFile, span, TopSection
} from "lqx-react/lib";
import { Button, colors, navigate } from "lqx-react/src";
import React, { useCallback, useEffect, useState } from "react";
import { v4 as uuidv4 } from 'uuid';
import { RowH1 } from "../../../../../common";
import { getCompanies } from "../api/company";
import { ConnectionDTO, EncryptionMethodType } from "../api/connectionDTO";
import { DirectoryPath } from "../api/connectionSettings";
import { Directories } from "../api/directories";
import { getAuthorizationBearer, getPlatformCode } from "../api/endpoint";
import { CancelButton, ConfirmationButton, DigiCheckbox, ErrorHighlighter, WorkflowFooter } from "../shared";
import { ConnectionsPathCard } from "./components/connections-path-card";
import { DestinationTargets } from "./components/destination-targets";
import { FilePathPickerModal } from "./components/file-path-picker";
import { MultiSelectDropdown } from "./components/multiselect-dropdown";
import { ConnectionsStore } from "./models/connections-store";

export const ConnectionsEditPage = ({ id }: { id?: string }) => {
    const classes = useStyles();
    const state = useModel(ConnectionsStore);
    const connection = useModel(ConnectionDTO);
    const $connectionInfo = useLinked({ status: '', message: '' });
    const $showErrors = useLinked(false);
    const $errorMessage = useLinked('');
    const directories = useModel(Directories);
    const $filePathPicker = useLinked(false);
    const $email = useLinked('');
    const [selectedCompany, setSelectedCompany] = useState<any>();
    const isEdit = id ? true : false;
    const isReady = useIO(async () => {
        const result = await Promise.all<any>([
            await state.flowTypes.fetch(),
            await state.channelTypes.fetch(),
            await state.platforms.fetch(),
            await state.products.fetch(),
            await state.encryptionMethods.fetch(),
            isEdit ? await connection.get(id).then() : null
        ]);
        if (isEdit) {
            setSelectedCompany({ id: result[5].customerCompanyId, name: result[5].customerCompanyName });
            connection.settings.paths.forEach(p => p.uniqueIndex = uuidv4());

        }
        state.initialize();
    }, []);

    connection.settings.paths.onChanges(() => {
        if (connection.settings.paths.length === 0) {
            connection.isActive = false;
        }
    })

    const getItems = useCallback(async (searchText) => {
        return (await getCompanies(searchText)).items;
    }, [])

    const save = async () => {
        if (connection.isValid()) {
            await connection.save().then(() => {
                connection.dispose();
                navigateBack();
            })
                .catch(err => {
                    $showErrors.set(true);
                    $errorMessage.set(err.message);
                });
        }
    }

    useEffect(() => {
        async function funcAsync() {
            !selectedCompany || connection.customerCompanyId !== selectedCompany.id ? connection.settings.paths.set([]) : null;
            connection.customerCompanyId = selectedCompany ? selectedCompany.id : null;
            selectedCompany ?
                await state.importTemplates.fetch({ params: { companyId: selectedCompany.id } })
                : state.importTemplates.items.set([]);
        }
        funcAsync();
    }, [selectedCompany])

    useEffect(() => {
        connection.settings.paths.set([]);
    }, [connection.channelTypeId['_changeToken']])

    const checkConnection = async () => {
        $connectionInfo.set({ status: 'inProggress', message: '' });

        try {
            const response = await connection.checkConnection()
            if (response && response.isConnected) {
                await directories.getDirectoriesList({
                    host: connection.settings.host,
                    port: connection.settings.port,
                    userName: connection.settings.userName,
                    password: connection.settings.password,
                    customerCompanyId: connection.customerCompanyId,
                    encryptionMethodId: connection.encryptionMethodId,
                    file: connection.file,
                    connectionId: connection.id
                })
                $showErrors.set(true);
                $connectionInfo.set({ status: 'success', message: 'Success!' });
            } else {
                $connectionInfo.set({ status: 'error', message: 'Failure' });
            }
        } catch (error) {
            $connectionInfo.set({ status: 'error', message: 'Failure' });
            $showErrors.set(true);
            $errorMessage.set(error.message);

        }
    }

    const navigateBack = () => navigate('connections');

    const flowTypes = () => state && state.flowTypes && state.flowTypes.items ? state.flowTypes.items.filter((flowType) => flowType.id !== '0') : []

    const handleCloseSnackbar = (_, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        $showErrors.set(false);
        $errorMessage.set('');
    };

    const disableOpenFilePathBrowserButton = () => $connectionInfo.value.status !== 'success';

    const addNewPath = (newPath: string = '', emailFrom: string = '') => {
        if (emailFrom) {
            const validatedResult = validateEmail(emailFrom);
            if (!validatedResult) {
                $showErrors.set(true);
                $errorMessage.set('You must enter a valid email address');
                return;
            }
        }
        connection.settings.paths.push({
            uniqueIndex: uuidv4(),
            isActive: true,
            path: newPath,
            emailFrom: emailFrom,
            destinationTargets: [] as any,
        });
    }

    const validateEmail = (email) => {
        const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return pattern.test(email);
    }

    const removePath = (dp: DirectoryPath) => {
        const notDeletedPathes = connection.settings.paths.filter((p) => p.uniqueIndex != dp.uniqueIndex);
        connection.settings.paths.set(notDeletedPathes)
    }

    const SaveConnectionButton = ({ onClick }) => {
        const $isOpen = useLinked(false);
        return <>
            <ConfirmationButton disabled={$isOpen.value || !connection.isValid() || !connection.isValidForConnect()} onClick={$isOpen.true} >Save Connection</ConfirmationButton>
            {$isOpen.value ? <ConfirmationDialog $isOpen={$isOpen} onClick={onClick} /> : null}
        </>
    }

    const getFileCodeForPath = (path) => {
        if (state.importTemplates && state.importTemplates.items.length && path.importTemplateId && !path.useStandardMapping) {
            const foundTemplate = state.importTemplates.items.find(_ => _.id === path.$.importTemplateId.value);
            return foundTemplate ? foundTemplate.assetType : '';
        }
        return path.fileCode ? path.fileCode : '';
    }

    return isReady ? (
        <Page maxWidth={false}>
            <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={$showErrors.value} autoHideDuration={3000} onClose={handleCloseSnackbar}>
                <Alert severity={$errorMessage.value ? 'error' : 'success'}>
                    <AlertTitle>{$errorMessage.value ? 'Error Happened' : 'Connected'}</AlertTitle>
                    {$errorMessage.value}
                </Alert>
            </Snackbar>
            <Container>
                <Flex row>
                    <RowH1 className={classes.title}>
                        <span style={{ marginRight: '20px' }}>{isEdit ? 'Edit' : 'Create a new'} connection</span>
                        <StyledFormControlLabel
                            control={
                                <Switch
                                    {...connection.$.isActive.props}
                                    disabled={connection.settings.paths.length === 0}
                                    color="primary"
                                />
                            }
                            label={"Active"}
                            labelPlacement="start"
                        />
                    </RowH1>
                </Flex>
                <TopSection id="connection-details" title="1. Select a Customer and Enter a Name for your Connection" >
                    <Flex row>
                        <Column>
                            <StyledFormLabel>Connection Name*</StyledFormLabel>
                            <ErrorHighlighter>
                                <StyledInput data-qa-id="connection name" $value={connection.$.name} aria-label="Connection Name" />
                            </ErrorHighlighter>
                        </Column>
                        <Column>
                            <StyledFormLabel>Flow direction*</StyledFormLabel>
                            <ErrorHighlighter>
                                <FormSelect data-qa-id="flow direction" $value={connection.$.flowTypeId} >
                                    {flowTypes().map((sm) => (<Option key={sm.id} value={sm.id}>{sm.name}</Option>))}
                                </FormSelect>
                            </ErrorHighlighter>
                        </Column>
                        <Column>
                            <StyledFormLabel>Customer*</StyledFormLabel>
                            <ErrorHighlighter>
                                <MultiSelectDropdown
                                    getItems={getItems}
                                    selectedItems={selectedCompany}
                                    handleChange={(selectedItem) => { setSelectedCompany(selectedItem) }}
                                    isMultiple={false}
                                />
                            </ErrorHighlighter>
                        </Column>
                    </Flex>
                </TopSection>
                <TopSection id="channel-details" title="2. Select a data channel for you Connection and set the polling frequency" >
                    <Flex row>
                        <Column>
                            <StyledFormLabel>Channel*</StyledFormLabel>
                            <FormSelect $value={connection.$.channelTypeId}>
                                {state.channelTypes.items.map((sm) => (sm.name !== 'API' && <Option key={sm.id} value={sm.id}>{sm.name}</Option>))}
                            </FormSelect>
                        </Column>
                        <Column></Column>
                        <Column></Column>
                        <Column></Column>
                        <Column></Column>
                    </Flex>
                    {connection.channelTypeId === +(state.channelTypes.items.find(i => i.name === 'EMAIL').id) ?
                        <>
                            <Flex row>
                                <EmailPathContainer row> Email Address*: </EmailPathContainer>
                            </Flex>
                            <Flex row>
                                <Column>
                                    <ErrorHighlighter>
                                        <StyledInput data-qa-id="server name" $value={$email} aria-label="Server Name" />
                                    </ErrorHighlighter>
                                </Column>
                                <Column>
                                    <Button
                                        color="primary"
                                        style={{ flex: 'inherit', alignSelf: 'center' }}
                                        disabled={$email.value === ''}
                                        onClick={() => {
                                            addNewPath('', $email.value);
                                            $email.set('');
                                        }}>Add</Button>
                                </Column>
                                <Column></Column>
                                <Column></Column>
                                <Column></Column>
                            </Flex>
                        </> :
                        <Flex row>
                            <Column>
                                <StyledFormLabel>Server Name*</StyledFormLabel>
                                <ErrorHighlighter>
                                    <StyledInput data-qa-id="server name" $value={connection.settings.$.serverName} aria-label="Server Name" />
                                </ErrorHighlighter>
                            </Column>
                            <Column style={{ flex: "1.6" }}>
                                <StyledFormLabel>Host Address*</StyledFormLabel>
                                <ErrorHighlighter>
                                    <StyledInput data-qa-id="host address" $value={connection.settings.$.host} aria-label="Host Address" />
                                </ErrorHighlighter>
                            </Column>
                            <Column style={{ flex: "0.45" }}>
                                <StyledFormLabel>Port Number*</StyledFormLabel>
                                <ErrorHighlighter>
                                    <StyledInput $value={connection.settings.$.port} aria-label="Port Number" />
                                </ErrorHighlighter>
                            </Column>
                            <Column></Column>
                            <Column></Column>
                        </Flex>
                    }
                    {connection.channelTypeId === +(state.channelTypes.items.find(i => i.name === 'SFTP').id) &&
                        <Flex row>
                            <Column>
                                <StyledFormLabel>Encryption Method*</StyledFormLabel>
                                <ErrorHighlighter>
                                    <FormSelect $value={connection.$.encryptionMethodId} >
                                        {state.encryptionMethods.items.map((sm) => (<Option key={sm.id} value={sm.id}>{sm.name}</Option>))}
                                    </FormSelect>
                                </ErrorHighlighter>
                            </Column>
                            <Column>
                                <StyledFormLabel>Username*</StyledFormLabel>
                                <ErrorHighlighter>
                                    <StyledInput data-qa-id="username" $value={connection.settings.$.userName} aria-label="Username" />
                                </ErrorHighlighter>
                            </Column>
                            <Column>
                                {EncryptionMethodType.PrivateKey === connection.encryptionMethodId &&
                                    <SshPrivateKey $file={connection.$.file} sshPrivateKeyFileName={connection.sshPrivateKeyFileName} />
                                }
                                {EncryptionMethodType.Password === connection.encryptionMethodId &&
                                    <>
                                        <StyledFormLabel>Password*</StyledFormLabel>
                                        <ErrorHighlighter>
                                            <StyledInput data-qa-id="password" type="password" $value={connection.settings.$.password} aria-label="Password" />
                                        </ErrorHighlighter>
                                    </>
                                }
                            </Column>
                            <Column>
                                <SubSection>
                                    <Flex row>
                                        <Column>
                                            <ConnectButton style={{ width: '80%' }} disabled={!connection.isValidForConnect()} onClick={checkConnection}>Connect</ConnectButton>
                                        </Column>
                                        <Column>
                                            {
                                                $connectionInfo.value.status === 'inProggress' ?
                                                    <LoadingContainer >
                                                        <Loading />
                                                    </LoadingContainer> : <></>
                                            }
                                            {
                                                $connectionInfo.value.status === 'success' ?
                                                    <Success><CheckIcon /></Success> : <></>
                                            }
                                            {
                                                $connectionInfo.value.status === 'error' ?
                                                    <Error><ErrorOutlineIcon /></Error> : <></>
                                            }
                                        </Column>
                                    </Flex>
                                </SubSection>
                            </Column>
                            <Column></Column>
                        </Flex>
                    }
                    <SubSection>
                        {connection.channelTypeId === +(state.channelTypes.items.find(i => i.name === 'SFTP').id) &&
                            <Flex row>
                                <StyledFormLabel style={{ flex: 'inherit', alignSelf: 'center' }}>Select the file path(s) you wish to consume asset data from:</StyledFormLabel>
                                <PrimaryButton disabled={disableOpenFilePathBrowserButton()} onClick={() => $filePathPicker.set(true)}>Browse</PrimaryButton>
                            </Flex>
                        }
                        {connection.settings.paths.length ? <>
                            <Flex row>
                                <StyledFormLabel style={{ marginTop: '10px' }}>Selected {connection.channelTypeId === +(state.channelTypes.items.find(i => i.name === 'SFTP').id) ? 'Paths' : 'Emails'}:</StyledFormLabel>
                            </Flex>
                            {connection.settings.paths.map((path) =>
                                <Flex row>
                                    <PathContainer>
                                        <FilePathContainer style={{ flex: 'inherit', alignSelf: 'center' }}>{connection.channelTypeId === +(state.channelTypes.items.find(i => i.name === 'SFTP').id) ? 'File Path:' : ''} {path.emailFrom || path.path}</FilePathContainer>
                                        <DigiCheckboxContainer>
                                            <DigiCheckbox label="Apply Asset Modifications" $checked={path.$.modifyExistingAssets} />
                                        </DigiCheckboxContainer>
                                        {
                                            getFileCodeForPath(path).toLowerCase() === "remittance-advice" &&
                                            <DigiCheckboxContainer>
                                                <DigiCheckbox label="Apply Merge RA Line Items" $checked={path.$.mergeExistingAssets} />
                                            </DigiCheckboxContainer>
                                        }
                                        <Button color="primary" onClick={() => { removePath(path) }}>Delete</Button>
                                    </PathContainer>
                                </Flex>
                            )}
                        </> : <></>}
                    </SubSection>
                </TopSection>

                <TopSection id="paths-to-templates" title="3. Select Import Template to apply to your files" >
                    {connection.settings.paths.map((path, i) =>
                        <ConnectionsPathCard
                            key={i}
                            path={path}
                            importTemplates={state.importTemplates}
                            customerCompanyId={connection.$.customerCompanyId.value}
                        ></ConnectionsPathCard>
                    )}
                </TopSection>

                <TopSection id="parsed-asset-data" title="4. Define the Initial routing requirements for the parsed Asset data" >
                    {connection.settings.paths.map((path, i) =>
                        <DestinationTargets
                            key={i}
                            path={path}
                            products={state.products}
                            platforms={state.platforms}
                        ></DestinationTargets>)}
                </TopSection>

                {$filePathPicker.value && <FilePathPickerModal
                    title={connection.settings.host}
                    directories={directories}
                    disabledIds={connection.settings.paths.map((path) => path.path)}
                    onClose={() => $filePathPicker.set(false)}
                    onConfirm={(val) => {
                        $filePathPicker.set(false);
                        addNewPath(val);
                    }}></FilePathPickerModal>}

                <WorkflowFooter>
                    <Flex row className={classes.footerActionButtons}>
                        <CancelButton color="primary" onClick={navigateBack}>Back</CancelButton>
                        <SaveConnectionButton onClick={save}></SaveConnectionButton>
                    </Flex>
                </WorkflowFooter>
            </Container>
        </Page>) : <Loading />
}

const ConfirmationDialog = ({ $isOpen, onClick }) => {
    return (
        <PopupIODialog
            $isOpen={$isOpen}
            title={"Save changes to Connection"}
            actionLabel="Yes"
            success={"Connection Saved"}
            hideTitleOnSuccess={true}
            disabled={false}
            doIO={onClick}
        ><span>Are you sure you want to save changes to this Connection?</span></PopupIODialog>
    )
}

const SshPrivateKey = ({ $file, sshPrivateKeyFileName }: { $file?: Linked<File>, sshPrivateKeyFileName?: string }) => {
    const getSshPrivateKeyName = () => {
        let fileName = sshPrivateKeyFileName ? sshPrivateKeyFileName : 'Click to select the ssh private key file...';

        if (Boolean($file.value)) {
            fileName = $file.value.name;
        }

        return fileName;
    }

    return (
        <>
            <StyledFormLabel>SSH Key*</StyledFormLabel>
            <Flex row>
                <SshKeyContainer>
                    <ErrorHighlighter>
                        <StyledInput disabled value={getSshPrivateKeyName()} />
                    </ErrorHighlighter>
                </SshKeyContainer>
                <UploadIconContainer>
                    <UploadSshKeyFileIcon $value={$file} accept='.txt' />
                </UploadIconContainer>
            </Flex>
        </>
    )
}

const DownloadSshPrivateKey = ({ file, fileName, companyId }: { file?: File, fileName?: string, companyId?: number }) => {
    const getBlob = async () => {
        const response = await fetch(`/api/v1/ingest/connections/file?companyId=${companyId}`, {
            method: 'GET',
            headers: new Headers({
                "Authorization": getAuthorizationBearer(),
                'X-Platform-Code': getPlatformCode()
            })
        });

        return await response.blob();
    }

    const download = async () => {
        const blob = file ? file : await getBlob();
        const dataUrl = window.URL.createObjectURL(blob),

            a = document.createElement('a');
        a.href = dataUrl;
        a.download = file ? file.name : fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    return <DownloadSshKeyFileButton onClick={() => download()}>Download file</DownloadSshKeyFileButton>
}

const useStyles = makeStyles({
    title: {
        paddingBottom: 20,
        color: '#3d6889',
        fontWeight: 600
    },
    footerActionButtons: {
        marginLeft: 'auto'
    },
    page: {
        marginLeft: '10px'
    }
});

const Container = styled('div')({
    marginTop: 20,
    paddingTop: 30,
    paddingRight: 30,
    paddingLeft: 30,
    paddingBottom: 60,
    background: 'white'
})

const PathContainer = div({
    display: 'flex',
    padding: '4px',
    '&:hover': {
        fontWeight: 800
    }
})

const StyledFormLabel = styled(FormLabel)({
    alignSelf: 'inherit',
    paddingLeft: 4
})

const StyledInput = styled(Input)({
    marginTop: 4,
    marginBottom: 4,
    height: 32
})

const ConnectButton = styled(PrimaryButton)({
    border: '0px',
    color: 'white',
    borderRadius: '10px',
    height: '25px',
    width: '200px',
});

const LoadingContainer = div({
    marginTop: '3px',
    marginLeft: '5px',
    maxWidth: '20px',
    maxHeight: '20px',
    '& .MuiCircularProgress-root': {
        maxWidth: '20px',
        maxHeight: '20px',
    }
});

const Error = div({
    color: '#FF274B'
});

const Success = div({
    color: '#4197d6'
});

const SubSection = div({
    paddingTop: '24px'
});

const UploadSshKeyFileIcon = styled(SelectFile)({
    border: 'none',
    padding: 0,
    '& .MuiSvgIcon-root': {
        fontSize: 'x-large'
    },
    '& .filename': {
        display: 'none'
    },
    cursor: 'pointer'
});

const StyledFormControlLabel = withStyles({
    label: {
        fontSize: '13px',
        fontFamily: 'Montserrat',
        fontWeight: 500,
        color: 'grey'
    },
    labelPlacementStart: {
        flexDirection: 'row-reverse',
        alignSelf: 'start',
        marginTop: 'auto',
        paddingLeft: 20,
        marginBottom: 0
    }
})(FormControlLabel);

const DownloadSshKeyFileButton = span({
    color: colors.blue,
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '12px',
    fontFamily: 'Montserrat'
});

const SshKeyContainer = div({
    flex: '1 0 auto'
});

const UploadIconContainer = div({
    flex: '0 0 auto',
    paddingLeft: '5px',
    alignSelf: 'center'
});

const FilePathContainer = div({
    color: '#052C4C',
    display: 'flex',
    fontSize: '14px',
    fontWeight: 600,
    textTransform: 'uppercase',
    paddingRight: '10px'
})

const EmailPathContainer = styled(Flex)({
    color: '#052C4C',
    display: 'flex',
    fontSize: '14px',
    fontWeight: 600,
    textTransform: 'uppercase',
    paddingRight: '10px'
})

const DigiCheckboxContainer = div({
    '& span': {
        color: '#767D84',
        fontSize: '12px',
        alignSelf: 'center',
        fontFamily: 'Montserrat',
        fontWeight: 500
    }
})
